import { Box, Center, Flex, Grid, Text } from "@chakra-ui/react";
import { AttendanceChatProps } from "../chats-context/types";
import { UserIcon } from "components/vectors/user-icon";
import { DoubleCheck } from "components/vectors/whatsapp-icons";
import {
  convenioColors,
  initialKanbanColumns,
  parseConvenio,
} from "components/atendimentos-components/kanban-and-table-atendimento/kanban/parts/utils";
import { TabulacaoType } from "components/atendimentos-components/kanban-and-table-atendimento/kanban/parts/types";
import api from "api/api";
import { Toast } from "components/toast";
import { Dropdown } from "components/dropdown";
import { dropdownTabulacao } from "components/atendimentos-components/atendimento-form/fields-data";
import { memo, ReactNode, useState } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { phoneFormat } from "utils/string-formats";
import {
  AttendanceActionsTypes,
  FormControl,
} from "contexts/attendance-context/actions-reducer";
import { useApplicationContext } from "contexts/ApplicationContext";
import { isUserGerente } from "utils/is-gerente";
import { openModalUserTransferAttendance } from "components/atendimentos-components/modal-change-user";
import { HeadPhoneIcon } from "components/vectors/headphone-icon";
import { DiagonalArrowIcon } from "components/vectors/diagonal-arrow";
import { BotIcon } from "components/vectors/bot-icon";
import { onClickAttendanceChatCard } from "./utils";
import { FaMicrophone, FaVideo } from "react-icons/fa";
import { MdPhotoCamera } from "react-icons/md";
import { PiStickerFill } from "react-icons/pi";
import { MessageType } from "../whats-app-chat/hooks/use-whats-app-session-controls/types";

export const greenWhatsapp = {
  500: "rgb(36, 211, 102)",
  600: "#1FA855",
  700: "#076058",
};
export const grayWhatsapp = {
  200: "rgb(240, 242, 245)",
  300: "rgb(223, 229, 231)",
  350: "#BCBCBC",
  400: "#667781",
  600: "#3b4a54",
};

function ConversationAttendanceCard({
  formControl,
  dispatch,
  selectedAttendance,
  setSelectedAttendance,
  ...props
}: AttendanceChatProps & {
  setAttendance: (data: AttendanceChatProps) => void;
  formControl: FormControl;
  dispatch: React.Dispatch<AttendanceActionsTypes>;
  selectedAttendance: AttendanceChatProps | undefined;
  setSelectedAttendance: React.Dispatch<
    React.SetStateAction<AttendanceChatProps | undefined>
  >;
}) {
  const [isLoading, setIsLoading] = useState(false);
  let {
    lastMessage,
    nome,
    etapa,
    convenio,
    tabulacao,
    id,
    customerId,
    customerName,
    setAttendance,
    unreadMessage,
    telefoneContatoNormalizado,
    usuarioNome,
  } = props;
  const lastMessageTime = lastMessage?.timestamp
    ? new Date(lastMessage?.timestamp! * 1000 || "")
    : null;

  const hours = lastMessageTime?.getHours();
  const minutes = lastMessageTime?.getMinutes();
  const lastMessageTimeFormat = lastMessage?.timestamp
    ? `${hours}:${minutes! < 10 ? "0" : ""}${minutes}`
    : null;

  const nameLimit = 18;
  const slicedName =
    nome?.length! > nameLimit ? nome?.slice(0, nameLimit).trim() + "..." : nome;

  const tabulacaoLabel = dropdownTabulacao.find(
    (option) => option.value === tabulacao
  )?.name;

  const { user } = useApplicationContext();
  const userType = user.userData.type;

  const isGERENTE = isUserGerente(user);

  const customerDescLimit = 30;
  const customer = `${
    userType === "PARCEIRO_GERENTE_ESTRATEGICO" ? `${customerName} - ` : ""
  }${customerName}`;

  const customerTagLabel = isGERENTE
    ? customer.length > customerDescLimit
      ? customer.slice(0, customerDescLimit) + "..."
      : customer
    : null;

  const userNameLimit = 25;
  const userNameLabel =
    (usuarioNome ?? "")?.length > userNameLimit
      ? usuarioNome?.slice(0, userNameLimit) + "..."
      : usuarioNome;

  const previewDesc = new Map<MessageType, ReactNode>([
    ["chat", lastMessage?.body],
    [
      "video",
      <Flex alignItems="center">
        <FaVideo />{" "}
        <Text as="span" ml="1.5">
          Vídeo
        </Text>
      </Flex>,
    ],
    [
      "audio",
      <Flex alignItems="center">
        <FaMicrophone />{" "}
        <Text as="span" ml="1.5">
          Áudio
        </Text>
      </Flex>,
    ],
    [
      "image",
      <Flex alignItems="center">
        <MdPhotoCamera />{" "}
        <Text as="span" ml="1.5">
          Foto
        </Text>
      </Flex>,
    ],
    [
      "sticker",
      <Flex alignItems="center">
        <PiStickerFill />{" "}
        <Text as="span" ml="1.5">
          Figurinha
        </Text>
      </Flex>,
    ],
  ]);

  return (
    <Flex
      bg={
        selectedAttendance?.id === props.id
          ? "#f5f6f6"
          : tabulacao === "IA_AGUARDANDO_HUMANO"
          ? "#FFEDBE"
          : usuarioNome === "VendeAI"
          ? "rgb(174, 239, 239, 0.8)"
          : "#fff"
      }
      _hover={{ bg: "#f5f6f6" }}
      onClick={() => {
        onClickAttendanceChatCard({
          dispatch,
          formControl,
          props,
          selectedAttendance,
          setSelectedAttendance,
          user,
        });
      }}
      cursor="pointer"
      borderLeft={`4px solid ${
        initialKanbanColumns.find((col) => col.colId === etapa)?.color ??
        "var(--chakra-colors-gray-500)"
      }`}
    >
      <Box w="100%" borderBottom={`1px solid ${grayWhatsapp[200]}`} pl="15px">
        <Grid
          alignItems="center"
          templateColumns="calc(49px + 8px) auto 49px"
          w="100%"
        >
          {/* Profile Photo */}
          <Box py="10px" pr="8px">
            <Center rounded="full" h="49px" w="49px" bg={grayWhatsapp[350]}>
              <UserIcon />
            </Center>
          </Box>
          {/* Name & last message */}
          <Flex
            flexDir="column"
            justifyContent="space-between"
            // h="calc(49px + 10px + 10px)"
            pr="8px"
            py="6px"
            h="126px"
          >
            <Flex
              alignItems="center"
              w="calc(100% + 49px)"
              borderBottom={`1px solid ${grayWhatsapp[200]}`}
              mr="-49px"
              pb="3px"
            >
              <Flex
                py="4px"
                px="8px"
                borderRadius="4px"
                alignItems="center"
                cursor="pointer"
                _hover={{ bg: "gray.100" }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openModalUserTransferAttendance({ id });
                }}
              >
                <Center mr="6px">
                  {usuarioNome === "VendeAI" ? (
                    <BotIcon
                      width={"17px"}
                      height={"14px"}
                      fill="currentColor"
                      fillOpacity={1}
                    />
                  ) : (
                    <HeadPhoneIcon />
                  )}
                </Center>
                <Text mr="5px" lineHeight="1.2">
                  {userNameLabel}
                </Text>
                <Box>
                  <DiagonalArrowIcon />
                </Box>
              </Flex>
            </Flex>
            {/* Name */}
            <Text
              fontWeight={unreadMessage ? "medium" : "normal"}
              fontSize={{ base: "15px", "2xl": "15px" }}
              color="#111b21"
            >
              {slicedName ||
                phoneFormat(telefoneContatoNormalizado, {
                  withCountryNumber: telefoneContatoNormalizado?.length! > 11,
                }) ||
                "(sem nome)"}
            </Text>
            <Box fontSize={{ base: "15px", "2xl": "15px" }} color="#111b21">
              {customerTagLabel ? (
                <Text fontSize="11px">{customerTagLabel}</Text>
              ) : null}
            </Box>
            {/*  */}
            {/* Last Message Preview */}
            <Flex
              alignItems="center"
              fontSize={{ base: "12px", "2xl": "13px" }}
              fontWeight={unreadMessage ? "medium" : "normal"}
              h="20px"
              color={unreadMessage ? grayWhatsapp[600] : grayWhatsapp[400]}
            >
              {lastMessage?.body ? (
                <Box mr="4px" transform="translateY(-1.5px)">
                  <DoubleCheck
                    width={{ base: "15px", "2xl": "16px" }}
                    height={{ base: "15px", "2xl": "16px" }}
                  />
                </Box>
              ) : null}
              <Box
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                maxW="calc(calc(calc(100vw - 100px) - 70.58vw) - 15px - 15px - 57px - 10px)"
                my="1"
                color="gray.500"
              >
                {lastMessage?.type !== "chat"
                  ? previewDesc.get(lastMessage?.type!)
                  : lastMessage?.body
                  ? lastMessage?.body
                  : null}
                &nbsp;
              </Box>
            </Flex>
            {/*  */}
            {/* Tag Blocks */}
            <Flex alignItems="center">
              {convenio ? (
                <Flex
                  alignItems="center"
                  borderRadius="6px"
                  fontWeight="medium"
                  fontSize={{ base: "9px", "2xl": "11px" }}
                  h="27px"
                  p={{ base: "2px 6px", "2xl": "3px 8px" }}
                  mr="8px"
                  lineHeight="1"
                  bg={convenioColors.get(convenio!) ?? "#F3EBA4"}
                >
                  {parseConvenio(convenio!)}
                </Flex>
              ) : null}
              <Dropdown
                onChange={(toTabulacao) => {
                  if (toTabulacao !== tabulacao) {
                    changeTabulacao({
                      fromTabulacao: tabulacao!,
                      toTabulacao,
                      attendanceId: id,
                      setAttendance,
                      setIsLoading,
                      atendimento: props,
                    });
                  }
                }}
                overflow="hidden"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                containerOptionProps={{
                  onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  },
                }}
                value={tabulacao}
                borderRadius="6px"
                options={dropdownTabulacao}
                border="1px solid #D5D5E5"
                fontWeight="medium"
                fontSize={{ base: "9px", "2xl": "11px" }}
                size="xs"
                h="27px"
                p={{ base: "2px 6px", "2xl": "3px 8px" }}
                variant="outline"
                iconSpacing="3px"
                menuListProps={{ fontSize: "12px" }}
                isLoading={isLoading}
                rightIcon={<ChevronDownIcon w="17px" h="17px" />}
              >
                {tabulacaoLabel?.length! > 7
                  ? tabulacaoLabel?.slice(0, 7) + "..."
                  : tabulacaoLabel}
              </Dropdown>
            </Flex>
          </Flex>
          {/* Time infos and unread icon */}
          <Flex
            flexDir="column"
            alignItems="flex-end"
            justifyContent="space-between"
            pr="15px"
            py="6px"
            h="100%"
          >
            <Text
              fontSize="12px"
              mb="4px"
              fontWeight="semibold"
              h="24px"
              color={unreadMessage ? greenWhatsapp[600] : grayWhatsapp[400]}
            >
              {lastMessageTimeFormat}
            </Text>
            <Center
              bg={unreadMessage ? greenWhatsapp[600] : undefined}
              w="20px"
              h="20px"
              rounded="full"
            ></Center>

            {etapa ? (
              <Flex
                mt="8px"
                alignItems="center"
                borderRadius="6px"
                fontWeight="medium"
                fontSize={{ base: "9px", "2xl": "11px" }}
                h="27px"
                p={{ base: "2px 6px", "2xl": "3px 8px" }}
                ml="8px"
                lineHeight="1"
                whiteSpace={{ base: "normal", "2xl": "nowrap" }}
                bg={
                  initialKanbanColumns.find((col) => col.colId === etapa)
                    ?.color ?? "#F3EBA4"
                }
                color="white"
              >
                {
                  initialKanbanColumns.find((col) => col.colId === etapa)
                    ?.colName
                }
              </Flex>
            ) : null}
          </Flex>
        </Grid>
      </Box>
    </Flex>
  );
}

export default memo(ConversationAttendanceCard, (prevProps, currProps) => {
  const propsCompare: any = {
    dataAtualizacao: prevProps.dataAtualizacao === currProps.dataAtualizacao,
    etapa: prevProps.etapa === currProps.etapa,
    nome: prevProps.nome === currProps.nome,
    telefoneContatoNormalizado:
      prevProps.telefoneContatoNormalizado ===
      currProps.telefoneContatoNormalizado,
    usuario: prevProps.usuarioNome === currProps.usuarioNome,
    mensagem: prevProps.lastMessage?.body === currProps.lastMessage?.body,
    timestamp:
      prevProps.lastMessage?.timestamp === currProps.lastMessage?.timestamp,
    convenio: prevProps.convenio === currProps.convenio,
    selectedAttendance:
      (prevProps.selectedAttendance?.id === prevProps.id) ===
      (currProps.selectedAttendance?.id === currProps.id),
    tabulacao: prevProps.tabulacao === currProps.tabulacao,
  };

  const isEquals = !Object.keys(propsCompare).find((k) => !propsCompare[k]);
  return isEquals;
});

const changeTabulacao = async ({
  fromTabulacao,
  toTabulacao,
  attendanceId,
  setAttendance,
  setIsLoading,
  atendimento,
}: {
  fromTabulacao: TabulacaoType | null;
  toTabulacao: TabulacaoType | null;
  attendanceId: number;
  setAttendance: (data: AttendanceChatProps) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  atendimento: AttendanceChatProps;
}) => {
  setIsLoading(true);
  try {
    await api.post(`/atendimentos/${attendanceId}/change-tabulacao`, {
      fromTabulacao,
      toTabulacao,
    });
    setAttendance({ ...atendimento, tabulacao: toTabulacao });
  } catch (e) {
    Toast({ title: "Erro ao tabular", status: "error" });
  } finally {
    setIsLoading(false);
  }
};
